<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    :maxWidth="'60%'"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("Profile") }}</v-tab>
      <v-tab>{{ $t("Login") }}</v-tab>
      <v-tab>{{ $t("RegionalSettings") }}</v-tab>
      <v-tab v-if="!isAdmin">{{ $t("Dashboard") }}</v-tab>
      <v-tab v-if="!isAdmin">{{ $t("DigitalSignature") }}</v-tab>
      <v-tab v-if="!isAdmin">{{ $t("Certificate") }}</v-tab>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-text-field
              v-bind:label="$t('FirstName')"
              v-model="user.name"
              maxlength="100"
              :counter="100"
              :error-messages="nameErrors"
              @input="$v.user.name.$touch()"
              @blur="$v.user.name.$touch()"
            ></v-text-field>

            <v-text-field
              v-bind:label="$t('Surname')"
              v-model="user.surname"
              maxlength="100"
              :counter="100"
              :error-messages="surnameErrors"
              @input="$v.user.surname.$touch()"
              @blur="$v.user.surname.$touch()"
            ></v-text-field>

            <v-text-field
              v-bind:label="$t('Email')"
              v-model="user.email"
              maxlength="100"
              :counter="100"
              :error-messages="emailErrors"
              @input="$v.user.email.$touch()"
              @blur="$v.user.email.$touch()"
            ></v-text-field>

            <v-text-field
              v-bind:label="$t('ReferenceNumber')"
              v-model="user.mobileNumber"
              :counter="100"
            ></v-text-field>
            <v-switch
                v-bind:label="$t('Site.UserConfig.UseSearchResultsTableView')"
                input-value="true"
                v-model="user.useSearchResultsTableView"
            />
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-text-field
              v-bind:label="$t('Login')"
              v-model="user.login"
              maxlength="100"
              :counter="100"
              :error-messages="loginErrors"
              @input="$v.user.login.$touch()"
              @blur="$v.user.login.$touch()"
              @change="user.changeLogin = true;"
            ></v-text-field>

            <v-text-field
              v-if="isNew"
              v-bind:label="$t('Password')"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword = !showPassword"
              :type="showPassword ? 'text' : 'password'"
              v-model="user.password"
              :error-messages="passwordErrors"
              @input="$v.user.password.$touch()"
              @blur="$v.user.password.$touch()"
              autocomplete="new-password"
            ></v-text-field>

            <v-select
              :disabled="!allowAccountTypeEdit"
              v-bind:label="$t('AccountType')"
              v-model="user.accountType"
              :items="accountTypesList"
              item-text="text"
              item-value="id"
            ></v-select>
            <v-row>
              <v-col cols="auto">
                <v-switch
                  v-bind:label="$t('TwoFactorAuthentication')"
                  input-value="true"
                  v-model="user.enable2FA"
                />
              </v-col>
              <v-col cols="auto">
                <v-switch
                  v-bind:label="$t('OnlyForDocECM')"
                  input-value="true"
                  v-show="user.enable2FA"
                  v-model="user.enable2FAOnlyDocECM"
                />
              </v-col>
            </v-row>
            <v-btn color="primary" v-if="!isNew" small @click="changePassword()">
                {{ $t("Site.UserConfig.ChangePassword") }}
            </v-btn>

            <v-text-field
              v-if="user.changePassword && user.id === currentUser.Id"
              v-bind:label="$t('Site.UserConfig.OldPassword')"
              :append-icon="showOldPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showOldPassword = !showOldPassword"
              :type="showOldPassword ? 'text' : 'password'"
              v-model="user.oldPassword"
              :error-messages="oldPasswordErrors"
              @input="$v.user.oldPassword.$touch()"
              @blur="$v.user.oldPassword.$touch()"
              autocomplete="new-password"
            ></v-text-field>

            <v-text-field
              v-if="user.changePassword"
              v-bind:label="$t('Password')"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword = !showPassword"
              :type="showPassword ? 'text' : 'password'"
              v-model="user.password"
              :error-messages="passwordErrors"
              @input="$v.user.password.$touch()"
              @blur="$v.user.password.$touch()"
              autocomplete="new-password"
            ></v-text-field>

            <v-text-field
              v-if="user.changePassword"
              v-bind:label="$t('ConfirmPassword')"
              :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showConfirmPassword = !showConfirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="user.confirmPassword"
              :error-messages="confirmPasswordErrors"
              @input="$v.user.confirmPassword.$touch()"
              @blur="$v.user.confirmPassword.$touch()"
              autocomplete="new-password"
            ></v-text-field>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-select
              v-bind:label="$t('Language')"
              v-model="user.languageId"
              :items="languageList"
              item-text="text"
              item-value="id"
              :error-messages="languageIdErrors"
              @change="$v.user.languageId.$touch()"
              @blur="$v.user.languageId.$touch()"
            ></v-select>
            <v-select
              v-bind:label="$t('TimeZone')"
              v-model="user.timeZoneId"
              :items="timeZoneList"
              item-text="text"
              item-value="id"
              :error-messages="timeZoneIdErrors"
              @change="$v.user.timeZoneId.$touch()"
              @blur="$v.user.timeZoneId.$touch()"
            ></v-select>
            <v-select
              v-bind:label="$t('DateFormat')"
              v-model="user.metricDateFormat"
              :items="dateFotmatList"
              item-text="text"
              item-value="id"
              :error-messages="metricDateFormatErrors"
              @change="$v.user.metricDateFormat.$touch()"
              @blur="$v.user.metricDateFormat.$touch()"
            ></v-select>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-bind:label="$t('DecimalSeparator')"
                  v-model="user.metricDecimalSep"
                  :items="decimalSeparatorList"
                  item-text="text"
                  item-value="id"
                  :error-messages="metricDecimalSepErrors"
                  @change="$v.user.metricDecimalSep.$touch()"
                  @blur="$v.user.metricDecimalSep.$touch()"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-bind:label="$t('ThousandSeparator')"
                  v-model="user.metricThousandSep"
                  :items="thousandSeparatorList"
                  item-text="text"
                  item-value="id"
                  :error-messages="metricThousandSepErrors"
                  @change="$v.user.metricThousandSep.$touch()"
                  @blur="$v.user.metricThousandSep.$touch()"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-switch
              v-bind:label="$t('PrivateDashboard')"
              input-value="true"
              v-model="user.privateDashboard"
            />
            <v-switch
              v-bind:label="$t('AllowDashboardImport')"
              input-value="true"
              v-if="allowAccountTypeEdit"
              v-model="user.allowImportDashboard"
            />
            <v-tooltip top :disabled="!isAdministratorOrganization">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-switch
                    v-on="on"
                    v-model="user.canEditDashboard"
                    :disabled="isAdministratorOrganization"
                    v-bind:label="$t('AllowDashboardEdit')"
                    input-value="true"
                  ></v-switch>
                </div>
              </template>
              <span>{{ $t("IsDefaultAllowDashboardEdit") }}</span>
            </v-tooltip>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <UserSignaturesList
          :signatures="user.signatures"
          :user="user"
          @signaturesChanged="signaturesChanged"
        ></UserSignaturesList>
      </v-tab-item>
      <v-tab-item>
        <v-file-input
          v-if="allowEditCertificate"
          v-bind:label="$t('Certificate')"
          v-model="signatureCertificate"
          accept=".pfx"
          @change="$v.user.signatureCertificate.$touch()"
          @blur="$v.user.signatureCertificate.$touch()"
        ></v-file-input>
        <v-text-field
          readonly
          v-if="!allowEditCertificate"
          v-model="user.signatureCertificateFileName"
          :label="$t('Certificate')"
          append-icon="mdi-pencil"
          @click:append="editCertificate()"
        ></v-text-field>

      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { required, email, not, sameAs, requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import VImageInput from 'vuetify-image-input/a-la-carte';
import UserService from '@/services/UserService';
import OrganizationService from '@/services/OrganizationService';
import User from '@/ecm/models/User';
import Utils from '@/ecm/common/Utils';
import AccountType from '@/ecm/variables/AccountType';
import UserSignaturesList from './UserSignaturesList';

export default {
    components: { UserSignaturesList, VImageInput },
    name: 'UserModal',
    props: {
        allowAccountTypeEdit: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        UserService.getLanguageList().then((data) => {
            this.languageList = data;
        });

        UserService.getTimeZoneList().then((data) => {
            this.timeZoneList = data;
        });

        UserService.getDateFormatList().then((data) => {
            this.dateFotmatList = data;
        });

        UserService.getDecimalSeparatorList().then((data) => {
            this.decimalSeparatorList = data;
        });

        UserService.getThousandSeparatorList().then((data) => {
            this.thousandSeparatorList = data;
        });

        this.$v.$reset();
    },
    data() {
        return {
            showPassword: false,
            showOldPassword: false,
            showConfirmPassword: false,
            languageList: [],
            timeZoneList: [],
            dateFotmatList: [],
            decimalSeparatorList: [],
            thousandSeparatorList: [],
            accountTypesList: [
                {
                    id: AccountType.FullAccess,
                    text: this.$t('FullAccessUsers'),
                },
                {
                    id: AccountType.Concurrent,
                    text: this.$t('ConcurrentUsers'),
                },
                {
                    id: AccountType.Readonly,
                    text: this.$t('ReadonlyUsers'),
                },
            ],
            selectedTab: 0,

            modal: {
                title: this.$t('User'),
                key: 'userModal',
            },
            open: false,
            user: new User(),
            allowEditCertificate: true, //Ugly fix for signature img in edition
            signatureCertificate: {},
            signatures: [],
            isNew: true,
            currentUser: this.$store.getters['account/user']
        };
    },
    mixins: [validationMixin],
    validations: {
        user: {
            name: { required },
            surname: { required },
            email: { required, email },
            login: { required },
            accountType: { required },
            oldPassword: {
                required: requiredIf(function() {
                    return this.user.changePassword && this.user.id === this.currentUser.Id;
                })
            },
            password: {
                required: requiredIf(function() {
                    return this.user.changePassword || this.isNew;
                }),
            },
            confirmPassword: {
                required: requiredIf(function() {
                    return this.user.changePassword;
                }),
                sameAsPassword: function(value) {
                    if (!this.user.changePassword) return true;
                    return value === this.user.password;
                }
            },
            languageId: { required },
            timeZoneId: { required },
            metricDateFormat: { required },
            metricDecimalSep: {
                required,
                notSame: not(sameAs('metricThousandSep'))
            },
            metricThousandSep: {
                notSame: not(sameAs('metricDecimalSep'))
            },
            signatureCertificate: {},
            canEditDashboard: {},
        },
    },
    computed: {
        isAdmin() {
            return (
                this.$store.getters['account/user'] &&
                this.$store.getters['account/user'].IsAdmin
            );
        },
        isSysLevel2() {
            return (
                this.$store.getters['account/user'] &&
                this.$store.getters['account/user'].SysLevel === 2
            );
        },
        isAdministratorOrganization() {
            return this.isAdmin || this.user.sysLevel == 2;
        },
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        modalTitle() {
            return (
                this.modal.title + (this.user.login ? ' - ' + this.user.login : '')
            );
        },
        signatureCertificateErrors() {
            const errors = [];
            if (!this.$v.user.signatureCertificate.$dirty) return errors;
            !this.$v.user.name.signatureCertificate &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        languageIdErrors() {
            const errors = [];
            if (!this.$v.user.languageId.$dirty) return errors;
            !this.$v.user.languageId.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        timeZoneIdErrors() {
            const errors = [];
            if (!this.$v.user.timeZoneId.$dirty) return errors;
            !this.$v.user.timeZoneId.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        metricDateFormatErrors() {
            const errors = [];
            if (!this.$v.user.metricDateFormat.$dirty) return errors;
            !this.$v.user.metricDateFormat.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        metricDecimalSepErrors() {
            const errors = [];
            if (!this.$v.user.metricDecimalSep.$dirty) return errors;
            !this.$v.user.metricDecimalSep.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        metricThousandSepErrors() {
            const errors = [];
            if (!this.$v.user.metricThousandSep.$dirty) return errors;
            !this.$v.user.metricThousandSep.notSame &&
                errors.push(this.$t('MetricThousandSepNotEqualToDecimalSep'));
            return errors;
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.user.name.$dirty) return errors;
            !this.$v.user.name.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        surnameErrors() {
            const errors = [];
            if (!this.$v.user.surname.$dirty) return errors;
            !this.$v.user.surname.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailErrors() {
            const errors = [];
            if (!this.$v.user.email.$dirty) return errors;
            !this.$v.user.email.email && errors.push('InvalidEmail');
            !this.$v.user.email.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        loginErrors() {
            const errors = [];
            if (!this.$v.user.login.$dirty) return errors;
            !this.$v.user.login.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        passwordErrors() {
            const errors = [];
            if (!this.$v.user.password.$dirty) return errors;
            !this.$v.user.password.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        oldPasswordErrors() {
            const errors = [];
            if (!this.$v.user.oldPassword.$dirty) return errors;
            !this.$v.user.oldPassword.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        confirmPasswordErrors() {
            const errors = [];
            if (!this.$v.user.confirmPassword.$dirty) return errors;
            !this.$v.user.confirmPassword.required && errors.push(this.$t('RequiredField'));
            !this.$v.user.confirmPassword.sameAsPassword && errors.push(this.$t('PasswordsDoNotMatch'));
            return errors;
        },
    },
    watch: {
        signatureCertificate() {
            if (this.signatureCertificate) {
                Utils.file.toBase64(this.signatureCertificate).then((base64) => {
                    this.user.signatureCertificateBase64 =
                        Utils.file.image.getBase64(base64).data;
                    this.user.signatureCertificateFileName =
                        this.signatureCertificate.name;
                });
            }
        },
    },
    methods: {
        async saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                if (
                    this.$v.user.name.$invalid ||
                    this.$v.user.surname.$invalid ||
                    this.$v.user.email.$invalid
                ) {
                    this.selectedTab = 0;
                } else if (
                    this.$v.user.login.$invalid ||
                    this.$v.user.accountType.$invalid
                ) {
                    this.selectedTab = 1;
                } else if (
                    this.user.changePassword &&
                    (
                        this.$v.user.password.$invalid ||
                        this.$v.user.oldPassword.$invalid ||
                        this.$v.user.confirmPassword.$invalid
                    )
                ) {
                    this.selectedTab = 1;
                } else if (
                    this.$v.user.languageId.$invalid ||
                    this.$v.user.timeZoneId.$invalid ||
                    this.$v.user.metricDateFormat.$invalid ||
                    this.$v.user.metricDecimalSep.$invalid ||
                    this.$v.user.metricThousandSep.$invalid
                ) {
                    this.selectedTab = 2;
                } else if (
                    this.$v.user.signatureImage.$invalid ||
                    this.$v.user.signatureCertificate.$invalid
                ) {
                    this.selectedTab = 3;
                }
                return;
            }

            if (this.user.id > 0 && (this.user.changePassword || this.user.changeLogin)) {
                const res = await this.$confirm(
                    this.$t('Site.UserConfig.LoginChangeDetected'),
                    {
                        title: this.$t('Site.UserConfig.DetectedChanges'),
                        icon: 'mdi-alert',
                        color: 'warning',
                        buttonTrueText: this.$t('Save'),
                        buttonFalseText: this.$t('Cancel'),
                    }
                );

                if (res === 2) {
                    return;
                }
            }

            this.modalInstance.progress(true);
            const dto = this.user.toDto();
            UserService.save(dto)
                .then((result) => {
                    if (this.isNew) {
                        Utils.toast.success(
                            this.$t('User') + ' ' + this.$t('CreatedSuccessfully')
                        );
                    } else {
                        Utils.toast.success(
                            this.$t('User') + ' ' + this.$t('UpdatedSuccessfully')
                        );
                    }
                    this.hide();
                    this.$emit('on-item-saved');
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        new() {
            this.user = new User();
            this.renderSignature = false;
            this.$v.$reset();
            OrganizationService.getSettings().then((settings) => {
                this.user.languageId = settings.DefaultLanguageID + '';
                this.user.timeZoneId = settings.DefaultTimezone;
                this.user.metricDateFormat = settings.DefaultDateFormat;
                this.user.metricDecimalSep = settings.DefaultDecimalSeparator;
                this.user.metricThousandSep = settings.DefaultThousandSeparator;
            });
            this.$nextTick(() => {
                this.renderSignature = true;
            });
            this.show();
        },
        edit(dto) {
            this.user = new User(dto);
            this.isNew = false;
            this.renderSignature = false;
            this.allowEditCertificate = true;
            this.user.changePassword = false;
            if (dto.SignatureCertificatePath) {
                this.allowEditCertificate = false;
            }
            /*
      if (dto.SignatureImagePath) {
        UserService.getSignatureImage(this.user.id).then((result) => {
          if (result) {
            this.user.signatureImage = Utils.file.image.toBase64(result);
            this.$nextTick(() => {
              this.renderSignature = true;
            });
          }
        });
      } else {
        this.$nextTick(() => {
          this.renderSignature = true;
        });
      }
      */

            this.show();
        },
        editUserFromGrid(userId) {
            UserService.getUserById(userId).then((result) => {
                this.edit(result);
            });
        },
        editCertificate() {
            this.allowEditCertificate = true;
            this.user.signatureCertificateFileName = '';
        },
        show() {
            this.open = true;
            this.selectedTab = 0;
        },
        hide() {
            this.open = false;
            this.selectedTab = 0;
            this.isNew = true;
            this.user.changePassword = false;
            this.$v.$reset();
        },
        signaturesChanged(signatures) {
            this.user.signatures = signatures;
        },
        changePassword() {
            this.user.changePassword = !this.user.changePassword;
            this.user.oldPassword = '';
            this.user.password = '';
            this.user.confirmPassword = '';
        },
    },
};
</script>
